"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { storyblokEditable, type SbBlokData } from "@storyblok/react/rsc";

type Asset = { filename?: string; alt?: string };

type BentoBoxGalleryBlok = SbBlokData & {
  image_1?: Asset;
  image_2?: Asset;
  image_3?: Asset;
  image_4?: Asset;
};

export default function BentoBoxGallery({ blok }: { blok: BentoBoxGalleryBlok }) {
  // Desktop bento spans on a 10-column grid: 6 + 4 on row 1, 3 + 7 on row 2.
  const cells = [
    { image: blok.image_1, span: "lg:col-span-6" },
    { image: blok.image_2, span: "lg:col-span-4" },
    { image: blok.image_3, span: "lg:col-span-3" },
    { image: blok.image_4, span: "lg:col-span-7" },
  ].flatMap((c) =>
    c.image?.filename ? [{ filename: c.image.filename, alt: c.image.alt ?? "", span: c.span }] : [],
  );

  const scrollerRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isScrollable, setIsScrollable] = useState(false);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const update = () => {
      const rect = scroller.getBoundingClientRect();
      const center = rect.left + rect.width / 2;
      let closest = 0;
      let minDist = Infinity;
      cardRefs.current.forEach((card, i) => {
        if (!card) return;
        const cRect = card.getBoundingClientRect();
        const cCenter = cRect.left + cRect.width / 2;
        const dist = Math.abs(cCenter - center);
        if (dist < minDist) {
          minDist = dist;
          closest = i;
        }
      });
      const maxScroll = scroller.scrollWidth - scroller.clientWidth;
      if (scroller.scrollLeft <= 1) closest = 0;
      else if (scroller.scrollLeft >= maxScroll - 1) closest = cells.length - 1;
      setActiveIndex(closest);
      setIsScrollable(scroller.scrollWidth > scroller.clientWidth + 4);
    };

    update();
    scroller.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      scroller.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [cells.length]);

  if (cells.length === 0) return null;

  return (
    <section {...storyblokEditable(blok)} className="bg-white">
      <div className="mx-auto w-full max-w-[1280px] px-[clamp(1.25rem,4vw,2.5rem)] py-[clamp(2.5rem,5vw,5rem)]">
        <div
          ref={scrollerRef}
          className="flex gap-4 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] lg:grid lg:aspect-1199/866 lg:grid-cols-10 lg:grid-rows-[503fr_343fr] lg:gap-5 lg:overflow-visible [&::-webkit-scrollbar]:hidden"
        >
          {cells.map((cell, i) => (
            <div
              key={i}
              ref={(el) => {
                cardRefs.current[i] = el;
              }}
              className={`relative aspect-4/3 w-[80%] shrink-0 overflow-hidden rounded-[16px] lg:aspect-auto lg:w-auto lg:rounded-[24px] ${cell.span}`}
            >
              <Image
                src={cell.filename}
                alt={cell.alt}
                fill
                sizes="(min-width: 1115px) 60vw, 80vw"
                className="object-cover"
              />
            </div>
          ))}
        </div>

        {isScrollable && cells.length > 1 && (
          <div className="mt-6 flex justify-center lg:hidden">
            <div className="flex items-center">
              {cells.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => {
                    cardRefs.current[i]?.scrollIntoView({
                      behavior: "smooth",
                      inline: "center",
                      block: "nearest",
                    });
                  }}
                  aria-label={`Go to image ${i + 1}`}
                  className="flex h-6 min-w-6 cursor-pointer items-center justify-center"
                >
                  <span
                    aria-hidden
                    className={`block h-1.5 rounded-full transition-all duration-300 ${
                      activeIndex === i ? "bg-purple-03 w-8" : "bg-purple-01 w-2"
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
